/**
 * FormNavigation Component - Story 6.3: Create Add Product Form
 * Navigation controls for multi-step form
 * Note: Submit button removed from step 3 as it's handled in the submit section
 */

import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import './FormNavigation.css'

export interface FormNavigationProps {
  currentStep: number
  totalSteps: number
  onNext: () => void | Promise<void>
  onPrevious: () => void
  onSubmit: () => void | Promise<void>
  onCancel: () => void
  isLoading?: boolean
  isSubmitting?: boolean
}

/**
 * FormNavigation Component
 * Provides navigation buttons for stepping through the form
 * On the last step (Images), only Cancel and Previous buttons are shown
 * as the Submit button is part of the submit summary section
 *
 * @component
 * @example
 * <FormNavigation
 *   currentStep={2}
 *   totalSteps={3}
 *   onNext={handleNext}
 *   onPrevious={handlePrevious}
 *   onSubmit={handleSubmit}
 *   onCancel={handleCancel}
 * />
 */
export function FormNavigation({
  currentStep,
  totalSteps,
  onNext,
  onPrevious,
  onCancel,
  isLoading = false,
}: FormNavigationProps) {
  const isLastStep = currentStep === totalSteps
  const isFirstStep = currentStep === 1

  return (
    <div className="form-navigation">
      <div className="navigation-buttons">
        {/* Cancel Button */}
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          className="cancel-button"
          aria-label="Cancel and go back"
        >
          Cancel
        </Button>

        {/* Previous Button */}
        {!isFirstStep && (
          <Button
            type="button"
            variant="outline"
            onClick={onPrevious}
            disabled={isLoading}
            className="flex items-center gap-2"
            aria-label="Go to previous step"
          >
            <ChevronLeft size={16} aria-hidden="true" />
            Previous
          </Button>
        )}

        {/* Next Button - only shown on steps 1 and 2 */}
        {!isLastStep && (
          <Button
            type="button"
            onClick={onNext}
            disabled={isLoading}
            className="flex items-center gap-2"
            aria-label="Go to next step"
          >
            Next
            <ChevronRight size={16} aria-hidden="true" />
          </Button>
        )}
      </div>

      {/* Step Progress Indicator */}
      <div className="step-progress-text">
        Step {currentStep} of {totalSteps}
      </div>
    </div>
  )
}
